import { useEffect, useState } from 'react';

import isEqual from 'lodash.isequal';

import { validateCode } from '../../api/rest/validateCode';
import { useAuth } from '@domain/auth/AuthContext';
import { ICoupon } from '@app-types/stripe';
import { isCouponExpired, isCouponPlanValid } from '@domain/premium/premium';

export interface IState {
  codeIsInvalid: boolean;
  coupon: ICoupon | undefined;
  isLoading: boolean;
}

export function useCoupon(couponCode: string | undefined): IState {
  const [couponState, setCouponState] = useState<IState>({
    codeIsInvalid: false,
    coupon: undefined,
    isLoading: false,
  });
  const { getToken } = useAuth();

  useEffect(() => {
    const handleSessionStorageChange = (): void => {
      const couponFromStorage = sessionStorage.getItem('subscription.coupon');
      const { data } = (couponFromStorage && JSON.parse(couponFromStorage)) ?? {};
      const { coupon } = couponState;

      if (!isEqual(data, coupon)) {
        setCouponState(prevState => ({ ...prevState, coupon: data }));
      }
    };

    window.addEventListener('couponInStorageChanged', handleSessionStorageChange);

    handleSessionStorageChange();

    return () => {
      window.removeEventListener('couponInStorageChanged', handleSessionStorageChange);
    };
  }, [couponState]);

  useEffect(() => {
    void (async (): Promise<void> => {
      if (!couponCode) {
        setCouponState(previousState => ({
          ...previousState,
          codeIsInvalid: false,
          coupon: undefined,
          isLoading: false,
        }));
        return;
      }

      setCouponState(previousState => ({ ...previousState, isLoading: true }));

      try {
        const coupon = await validateCode(couponCode, getToken);

        if (isCouponPlanValid(coupon) && !isCouponExpired(coupon)) {
          sessionStorage.setItem(
            'subscription.coupon',
            JSON.stringify({ code: couponCode, data: coupon }),
          );
          setCouponState(previousState => ({
            ...previousState,
            codeIsInvalid: false,
            coupon,
            isLoading: false,
          }));
        } else {
          sessionStorage.setItem('subscription.coupon', JSON.stringify(null));
          throw new Error(`Plan ${coupon.group.stripe_plan.external_id} for coupon not found`);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (__) {
        sessionStorage.setItem('subscription.coupon', JSON.stringify(null));
        setCouponState(previousState => ({
          ...previousState,
          codeIsInvalid: true,
          coupon: undefined,
          isLoading: false,
        }));
      }
    })();

    // We intentionally only want this to trigger when the couponCode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCode]);

  return couponState;
}
